import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { reportAPI, type ReportListItem } from '@/lib/api/reports'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'
import { LIVE_STATUSES } from '@/lib/report/liveStatus'
import { statusMeta } from '@/lib/report/statusLabels'
import { cn } from '@/lib/utils'

const REPORT_TOOLS = new Set(['create_report', 'write_section', 'finalize_report'])

const FILTERS = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'live', label: 'Live', match: (r: ReportListItem) => LIVE_STATUSES.has(r.status) },
  { key: 'complete', label: 'Complete', match: (r: ReportListItem) => r.status === 'complete' },
  { key: 'failed', label: 'Failed', match: (r: ReportListItem) => r.status === 'tables_failed' },
] as const
type FilterKey = (typeof FILTERS)[number]['key']

interface ReportLibraryProps {
  selectedId: string | null
  onOpen: (id: string) => void
  onNew: () => void
  events?: ToolCallEvent[]
  refreshKey: number
}

function projectLabel(r: ReportListItem): string | null {
  if (!r.projects.length) return null
  const extra = r.projects.length - 1
  return extra > 0 ? `${r.projects[0].name} +${extra}` : r.projects[0].name
}

function rowSubtitle(r: ReportListItem): string {
  const meta = statusMeta(r.status)
  const time = formatDistanceToNow(new Date(r.updated_at ?? r.created_at), { addSuffix: true })
  return [projectLabel(r), r.status === 'complete' ? null : meta.label, time]
    .filter(Boolean)
    .join(' · ')
}

/** Left pane of Reports mode: searchable, filterable owned-reports list. */
export function ReportLibrary({ selectedId, onOpen, onNew, events, refreshKey }: ReportLibraryProps) {
  const [reports, setReports] = useState<ReportListItem[]>([])
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<FilterKey>('all')
  const seenRef = useRef(new Set<string>())

  const fetchReports = () =>
    reportAPI
      .listReports()
      .then((r) => setReports(r.data.reports))
      .catch(() => toast.error('Could not load reports'))

  useEffect(() => {
    void fetchReports()
  }, [refreshKey])

  // Socket events are newest-first (prepends); refetch once per fresh completed report event.
  useEffect(() => {
    if (!events) return
    let fresh = false
    for (const e of events) {
      const key = `${e.call_id}:${e.data.status}`
      if (seenRef.current.has(key)) continue
      seenRef.current.add(key)
      if (e.type === 'tool_call' && REPORT_TOOLS.has(e.data.tool_name) && e.data.status === 'completed') {
        fresh = true
      }
    }
    if (fresh) void fetchReports()
  }, [events])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    const chip = FILTERS.find((f) => f.key === filter) ?? FILTERS[0]
    return reports.filter(
      (r) =>
        chip.match(r) &&
        (!q ||
          r.title.toLowerCase().includes(q) ||
          r.projects.some((p) => p.name.toLowerCase().includes(q))),
    )
  }, [reports, query, filter])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reports</span>
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={onNew}>
          <Plus className="h-3.5 w-3.5" /> New report
        </Button>
      </div>
      <div className="space-y-2 px-3 pb-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search reports…"
          className="h-7 text-xs"
        />
        <div className="flex gap-1" role="group" aria-label="Status filter">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs',
                filter === f.key
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted/40',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <ul className="divide-y divide-border overflow-y-auto">
        {visible.map((r) => {
          const meta = statusMeta(r.status)
          return (
            <li key={r.report_id}>
              <button
                type="button"
                onClick={() => onOpen(r.report_id)}
                aria-current={r.report_id === selectedId ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40',
                  r.report_id === selectedId && 'bg-muted/60',
                )}
              >
                <span
                  title={meta.label}
                  className={cn('h-2 w-2 shrink-0 rounded-full', meta.dotClass)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-foreground">{r.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">{rowSubtitle(r)}</span>
                </span>
              </button>
            </li>
          )
        })}
        {visible.length === 0 && (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground">
            {reports.length === 0
              ? 'No reports yet — create one, or ask the assistant.'
              : 'No reports match.'}
          </li>
        )}
      </ul>
    </div>
  )
}
